import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { formatDateBR, formatMoneyBR } from "../utils/format";

type PaymentModalProps = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  itemId: number;
  itemType: "payable" | "receivable";
  itemDescription: string;
  itemAmount: number;
  itemDueDate: string;
};

type PaymentMethod = {
  id: number;
  name: string;
  type: string;
  allowInstallments?: boolean;
  minInstallments?: number | null;
  maxInstallments?: number | null;
};

export const PaymentModal: React.FC<PaymentModalProps> = ({
  show,
  onClose,
  onSuccess,
  itemId,
  itemType,
  itemDescription,
  itemAmount,
  itemDueDate,
}) => {
  const { client } = useAuth();
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
  const [paidAmount, setPaidAmount] = useState("");
  const [interestAmount, setInterestAmount] = useState("0");
  const [fineAmount, setFineAmount] = useState("0");
  const [discountAmount, setDiscountAmount] = useState("0");
  const [description, setDescription] = useState("");
  const [installmentsCount, setInstallmentsCount] = useState(1);
  const [fiscalDocType, setFiscalDocType] = useState("");
  const [fiscalDocNumber, setFiscalDocNumber] = useState("");
  const [fiscalDocKey, setFiscalDocKey] = useState("");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPayable = itemType === "payable";
  const title = isPayable ? "Pagar Conta" : "Receber Conta";
  const amountLabel = isPayable ? "Valor Pago" : "Valor Recebido";
  const selectedMethod = paymentMethods.find((method) => method.id === paymentMethodId);
  const isCreditCard = selectedMethod?.type === "CARTAO_CREDITO";
  const allowsInstallments = Boolean(selectedMethod?.allowInstallments);
  const minInstallments = selectedMethod?.minInstallments ?? 1;
  const maxInstallments = selectedMethod?.maxInstallments ?? minInstallments;

  useEffect(() => {
    if (show) {
      // Resetar formulário
      setPaymentDate(new Date().toISOString().split("T")[0]);
      setPaidAmount(String(itemAmount));
      setInterestAmount("0");
      setFineAmount("0");
      setDiscountAmount("0");
      setDescription("");
      setInstallmentsCount(1);
      setFiscalDocType("");
      setFiscalDocNumber("");
      setFiscalDocKey("");
      setError(null);

      // Carregar formas de pagamento
      const fetchPaymentMethods = async () => {
        const response = await client.get("/modules/payment-methods?take=100");
        if (response.ok) {
          const data = await response.json();
          setPaymentMethods(data.data || []);
        }
      };
      fetchPaymentMethods();
    }
  }, [show, itemAmount, client]);

  useEffect(() => {
    if (!show) {
      return;
    }
    if (!isCreditCard || !allowsInstallments) {
      setInstallmentsCount(1);
      return;
    }
    const initial = Math.max(1, minInstallments);
    setInstallmentsCount((current) => {
      if (current < minInstallments || current > maxInstallments) {
        return initial;
      }
      return current || initial;
    });
  }, [allowsInstallments, isCreditCard, maxInstallments, minInstallments, show]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!paymentMethodId) {
        setError("Selecione uma forma de pagamento");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const payload: Record<string, unknown> = {
          paymentDate,
          paymentMethodId,
          [isPayable ? "paidAmount" : "receivedAmount"]: Number(paidAmount),
          interestAmount: Number(interestAmount),
          fineAmount: Number(fineAmount),
          discountAmount: Number(discountAmount),
        };
        if (isPayable && isCreditCard) {
          payload.installmentsCount = installmentsCount;
        } else {
          payload.paymentDescription = description;
        }
        if (isPayable) {
          const trimmedNumber = fiscalDocNumber.trim();
          const trimmedKey = fiscalDocKey.trim();
          const hasFiscalInfo = Boolean(
            fiscalDocType && (trimmedNumber || trimmedKey)
          );
          if (hasFiscalInfo) {
            payload.fiscalDocType = fiscalDocType;
            if (trimmedNumber) {
              payload.fiscalDocNumber = trimmedNumber;
            }
            if (trimmedKey) {
              payload.fiscalDocKey = trimmedKey;
            }
          }
        }

        const endpoint = isPayable
          ? `/modules/payments/payables/${itemId}/pay`
          : `/modules/payments/receivables/${itemId}/receive`;

        const response = await client.post(endpoint, payload);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || errorData.message || "Erro ao processar pagamento");
        }

        onSuccess();
        onClose();
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Erro ao processar pagamento";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [
      paymentDate,
      paymentMethodId,
      paidAmount,
      interestAmount,
      fineAmount,
      discountAmount,
      description,
      itemId,
      isPayable,
      client,
      onSuccess,
      onClose,
      isCreditCard,
      installmentsCount,
      fiscalDocType,
      fiscalDocNumber,
      fiscalDocKey,
    ]
  );

  const totalAmount = Number(paidAmount) + Number(interestAmount) + Number(fineAmount) - Number(discountAmount);
  const normalizedDueDate = itemDueDate ? itemDueDate.slice(0, 10) : "";
  const canApplyDueDate =
    Boolean(normalizedDueDate) && !Number.isNaN(new Date(normalizedDueDate).getTime());

  const handleSetPaymentDateAsDueDate = useCallback(() => {
    if (!canApplyDueDate) {
      return;
    }
    setPaymentDate(normalizedDueDate);
  }, [canApplyDueDate, normalizedDueDate]);

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Descrição</label>
                  <p className="text-muted mb-1">{itemDescription}</p>
                  <small className="text-muted">Valor original: {formatMoneyBR(itemAmount)}</small>
                  <br />
                  <small className="text-muted">Vencimento: {formatDateBR(itemDueDate)}</small>
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <label className="form-label mb-0">Data do Pagamento *</label>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleSetPaymentDateAsDueDate}
                      disabled={!canApplyDueDate || loading}
                      title={canApplyDueDate ? "Usar vencimento" : "Sem vencimento para aplicar"}
                    >
                      Usar vencimento
                    </button>
                  </div>
                  <input
                    type="date"
                    className="form-control"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Forma de Pagamento *</label>
                  <select
                    className="form-select"
                    value={paymentMethodId || ""}
                    onChange={(e) => setPaymentMethodId(Number(e.target.value))}
                    required
                    disabled={loading}
                  >
                    <option value="">Selecione...</option>
                    {paymentMethods.map((method) => (
                      <option key={method.id} value={method.id}>
                        {method.name} ({method.type})
                      </option>
                    ))}
                  </select>
                </div>
                {isCreditCard && (
                  <div className="alert alert-info">
                    Sera lancada na fatura do cartao selecionado.
                  </div>
                )}
                {isCreditCard && allowsInstallments && (
                  <div className="mb-3">
                    <label className="form-label">Parcelas</label>
                    <select
                      className="form-select"
                      value={installmentsCount}
                      onChange={(e) => setInstallmentsCount(Number(e.target.value))}
                      disabled={loading}
                    >
                      {Array.from(
                        { length: Math.max(1, maxInstallments - minInstallments + 1) },
                        (_, index) => minInstallments + index
                      ).map((value) => (
                        <option key={value} value={value}>
                          {value}x
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">{amountLabel} *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={paidAmount}
                      onChange={(e) => setPaidAmount(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Desconto</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Juros</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={interestAmount}
                      onChange={(e) => setInterestAmount(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Multa</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={fineAmount}
                      onChange={(e) => setFineAmount(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Observações</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={loading}
                  />
                </div>

                {isPayable && (
                  <div className="border rounded p-3 mb-3">
                    <div className="fw-semibold mb-2">Nota Fiscal (opcional)</div>
                    <div className="row g-3">
                      <div className="col-12 col-md-4">
                        <label className="form-label">Tipo</label>
                        <select
                          className="form-select"
                          value={fiscalDocType}
                          onChange={(e) => setFiscalDocType(e.target.value)}
                          disabled={loading}
                        >
                          <option value="">Selecionar</option>
                          <option value="NFE">NFE</option>
                          <option value="NFSE">NFSE</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Numero</label>
                        <input
                          type="text"
                          className="form-control"
                          value={fiscalDocNumber}
                          onChange={(e) => setFiscalDocNumber(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Chave</label>
                        <input
                          type="text"
                          className="form-control"
                          value={fiscalDocKey}
                          onChange={(e) => setFiscalDocKey(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="alert alert-info">
                  <strong>Total: {formatMoneyBR(totalAmount)}</strong>
                  <br />
                  {totalAmount < itemAmount && (
                    <small className="text-warning">⚠️ Pagamento parcial - status será alterado para PARTIAL</small>
                  )}
                  {totalAmount >= itemAmount && (
                    <small className="text-success">✓ Pagamento completo - status será alterado para {isPayable ? "PAID" : "RECEIVED"}</small>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? "Processando..." : "Confirmar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
    </>
  );
};
